import PropTypes from 'prop-types'
import { forwardRef, memo, useCallback, useMemo } from 'react'
import { useUserContext } from 'app/services/UserProvider/UserProvider'
import Button from 'components/core/Button/Button'
import useFallbackRef from 'lib/react/hooks/useFallbackRef'
import classNames from 'lib/util/classNames'
import noop from 'lib/util/noop'
import Spinner from '../Spinner/Spinner'
import styles from './ButtonActivity.module.scss'

/**
 * The `ButtonActivity`
 *
 * @param {object} props - the component props
 * @returns {React.ReactElement} the element
 */
const ButtonActivity = forwardRef((props, forwardedRef) => {
  const { children, className, isBusy: isBusyInput, onClick, ...rest } = props
  const { userActivityStatus } = useUserContext()
  const ref = useFallbackRef(forwardedRef)

  /**
   * Memo: The `isBusy` status, if there is no `isBusy` prop
   * supplied, assume it is just relying on the global
   * `userActivityStatus`
   */
  const isBusy = useMemo(() => {
    if (isBusyInput !== undefined) {
      return isBusyInput
    }

    return !!userActivityStatus
  }, [isBusyInput, userActivityStatus])

  const classNameOutput = useMemo(
    () => classNames([styles.container, className, isBusy && styles.busy]),
    [className, isBusy]
  )

  /**
   * Handle click
   */
  const handleClick = useCallback(
    e => {
      if (isBusy) {
        e.preventDefault()

        return
      }

      onClick()
    },
    [onClick, isBusy]
  )

  return (
    <Button
      ref={ref}
      className={classNameOutput}
      {...rest}
      isBusy={isBusy}
      onClick={handleClick}
    >
      <span className={styles.display}>
        <span className={styles.label}>{children}</span>
        {isBusy && <Spinner className={styles.spinner} delayShow={0} />}
      </span>
    </Button>
  )
})

// Display Name
ButtonActivity.displayName = 'ButtonActivity'

/** @type {object} */
ButtonActivity.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  isBusy: PropTypes.bool,
  onClick: PropTypes.func
}

/** @type {object} */
ButtonActivity.defaultProps = {
  children: null,
  className: '',
  isBusy: undefined,
  onClick: noop
}

// Memoize
export default memo(ButtonActivity)
